import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';

type Status = WriterVersionSummary['compile_status'];

/**
 * Emits a toast only when a writer version finishes compiling successfully.
 * Compile failures are intentionally NOT toasted here because the
 * WriterCompileStatusBanner already surfaces the full diagnostic log in the
 * project UI, and duplicate error notifications create noise.
 *
 * Non-invasive: purely observational. Reads the same `versions` array the
 * scrubber already renders and remembers the last seen status per id.
 */
export function useWriterCompileNotifications(
  versions: WriterVersionSummary[],
): void {
  const lastStatus = useRef<Map<string, Status>>(new Map());
  const primed = useRef(false);

  useEffect(() => {
    // First pass: seed the map without emitting toasts so mounting on a
    // page that already has succeeded versions stays quiet.
    if (!primed.current) {
      for (const v of versions) lastStatus.current.set(v.id, v.compile_status);
      primed.current = true;
      return;
    }

    for (const v of versions) {
      const prev = lastStatus.current.get(v.id);
      lastStatus.current.set(v.id, v.compile_status);
      if (prev === v.compile_status) continue;
      if (v.compile_status === 'success' && prev && prev !== 'success') {
        toast.success(`Compiled: ${v.label ?? 'version'}`);
      }
    }
  }, [versions]);
}
